import React from 'react';
import Grid from '@material-ui/core/Grid';
import { Autocomplete } from '@material-ui/lab';
import { AxiosError, AxiosResponse } from 'axios';
import TextField from '@material-ui/core/TextField';
import { Button, Icon, FormControlLabel, Checkbox } from '@material-ui/core';
import { Column, Options, Action } from 'material-table';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { JournalistContext } from '../JournalistContext';
import { JournalistCategoryModel, journalistCategoryModelInitial } from '../../../Models/JournalistCategoryModel';
import { getTableColumn, validateSpecialCharacter } from '../../../shared/operators';
import JournalistService from '../../../Services/JournalistService';
import JournalistCategoryService from '../../../Services/JournalistCategoryService';
import { TableLoading } from '../../../shared/components/TableLoading';
import { TableEmpty } from '../../../shared/components/TableEmpty';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { SearchStatus } from '../../../shared/enum/SearchStatus';
import { TableCommon } from '../../../shared/components/TableCommon';
import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';
import { JournalistModel, journalistModelInitial } from '../../../Models/JournalistModel';
import { HomeContext } from '../../Home/HomeContext';
import { BasicPage } from '../../../shared/components/BasicPage';
import { SearchButtons } from '../../../shared/components/SearchButtons';

const useStyles = makeStyles((theme: Theme) => createStyles({
    root: {
        display: 'flex',
        padding: theme.spacing(3),
        maxWidth: '1152px',
        width: '100%',
    },
    itens: {
        width: '100%',
        color: 'primary'
    },
    containerTitle:{
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    addNewButton: {
        paddingLeft: 0,
        '&:hover': {
            textDecorationLine: 'underline',
        },
    },
    tableGrid: {
        width: '100%'
    },
    statusCheckbox: {
        alignSelf: 'center'
    }
}));

export function JournalistSearch() {
    const classes = useStyles();

    const journalistContext = React.useContext(JournalistContext);
    const homeContext = React.useContext(HomeContext);

    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);
    const [journalistDelete, setJournalistDelete] = React.useState<JournalistModel>(journalistModelInitial);
    const [journalistName, setJournalistName] = React.useState<string>('');
    const [hasSpecialCharacter, setHasSpecialCharacter] = React.useState(false);
    const [journalistCategoriesList, setJournalistCategoriesList] = React.useState<JournalistCategoryModel[]>([]);
    const [searchStatus, setSearchStatus] = React.useState<SearchStatus>(SearchStatus.INITIAL);
    const [selectedCategory, setSelectedCategory] = React.useState<JournalistCategoryModel>(journalistCategoryModelInitial);
    const [showInactives, setshowInactives] = React.useState<boolean>(false);

    React.useEffect(() => {
        JournalistCategoryService.journalistCategoryService()
            .getAll()
            .then((res: AxiosResponse) => {
                if (res.data)
                    setJournalistCategoriesList(res.data);
            })
            .catch((error: AxiosError) => {
                homeContext.showError('Ops.. ocorreu um erro e não foi possível carregar as categorias.' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
            });
    }, []);

    const tableColumns: Column<JournalistModel>[] = [
        getTableColumn('Nome', 'name', 'string', 'left'),
        getTableColumn('Categoria', 'category.description', 'string', 'left'),
        getTableColumn('Peso', 'category.score', 'string', 'center')
    ];

    const tableActions: (Action<any> | ((rowData: any) => Action<any>))[] = [
        rowData => ({
            icon: 'delete_outline',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Deletar jornalista',
            onClick: (_, rowData: JournalistModel) => {
                setShowConfirmDelete(true);
                setJournalistDelete(rowData);
            },
            disabled: rowData.endOfPeriod != null,
            hidden: rowData.endOfPeriod != null
        }),
    ];

    const tableOptions: Options<JournalistModel> = {
        toolbar: false,
        showTitle: false,
        actionsColumnIndex: -1,
        pageSizeOptions: [10, 50, 100, 200],
        pageSize: 50,
    };
    
    const handleCleanFilter = () => {
        setJournalistName('');
        setSelectedCategory(journalistCategoryModelInitial);
        setshowInactives(false);
    };

    const handleSearch = () => {
        if(!hasSpecialCharacter) {
            setSearchStatus(SearchStatus.LOADING);

            JournalistService.getJournalistByQuery(journalistName, selectedCategory.id, showInactives)
                .then((res: AxiosResponse) => {
                    if(res.data) {
                        journalistContext.setJournalistList(res.data);

                        if(res.data.length > 0)
                            setSearchStatus(SearchStatus.RESULT);
                        else
                            setSearchStatus(SearchStatus.EMPTY);
                    }
                })
                .catch((error: AxiosError) => {
                    setSearchStatus(SearchStatus.INITIAL);
                    homeContext.showError('Ops.. ocorreu um erro e não foi possível carregar os jornalistas.' +
                        'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                });
        }
    };

    const deleteJournalist = () => {
        setShowConfirmDelete(false);

        JournalistService.journalistService().remove(journalistDelete.id)
            .then(() => {
                const journalists = [...journalistContext.journalistList];
                const index = journalists.findIndex(item => item.id == journalistDelete.id);
                journalists.splice(index, 1);
                journalistContext.setJournalistList(journalists);
                homeContext.showSuccess('O jornalista foi excluído com sucesso!');
            })
            .catch((error: AxiosError) => {
                homeContext.showError('Ops.. ocorreu um erro e não foi possível excluir o jornalista.' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
            });
    };
    
    const validateName = () => {
        setHasSpecialCharacter(validateSpecialCharacter(journalistName ?? ''));
    };

    const getName = () => {
        return (
            <Grid item xs={12} sm={5}>
                <TextField
                    label="Nome do jornalista"
                    variant="outlined"
                    fullWidth
                    value={journalistName}
                    onChange={ (event) => setJournalistName(event.target.value) }
                    className={classes.itens}
                    onBlur={validateName}
                    error={ hasSpecialCharacter }
                    helperText={hasSpecialCharacter ? 'Caracteres especiais não são permitidos, altere a sua pesquisa.' : ''}
                    inputProps={{ maxLength: 100 }} />
            </Grid>
        );
    };

    const getCategory = () => {
        return (
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    id="journalist-category"
                    value={selectedCategory}
                    onChange={(_, value: JournalistCategoryModel | null) => {
                        if (value)
                            setSelectedCategory(value);
                        else
                            setSelectedCategory(journalistCategoryModelInitial);
                    }}
                    options={journalistCategoriesList}
                    getOptionLabel={(option: JournalistCategoryModel) => option.description}
                    className={classes.itens}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) => 
                        <TextField 
                            {...params}
                            label="Categoria do jornalista"
                            variant="outlined" />
                    } />
            </Grid>
        );
    };

    const getStatus = () => {
        return (
            <Grid item xs={12} sm={3} className={classes.statusCheckbox}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showInactives}
                            onChange={ () => setshowInactives(!showInactives)}
                            name="Exibir jornalistas inativos"
                            color="primary"/>
                    }
                    label = "Exibir jornalistas inativos" />
            </Grid>
        );
    };

    const getAddNewButton = () => {
        return (
            <Grid item xs={12}>
                <Button
                    onClick={() => homeContext.setPageLocation(PageLocation.JOURNALIST_INPUT)}
                    size='large'
                    startIcon={<Icon color='primary'>add</Icon>}
                    className={classes.addNewButton}>
                    ADICIONAR NOVO JORNALISTA
                </Button>
            </Grid>
        );
    };

    const getDialogs = () => {
        return (
            <>
                <BasicConfirmDialog
                    title={'Tem certeza que deseja excluir este jornalista?'}
                    description={'Ao excluir este jornalista, ele ficará inativo para novas classificações.'}
                    handleFirstButton={() => deleteJournalist()}
                    handleSecondButton={() => setShowConfirmDelete(false)}
                    textFirstButton={'Excluir'}
                    textSecondButton={'Cancelar'}
                    open={showConfirmDelete}/>
            </>
        );
    };

    return (
        <BasicPage
            title="Consulta de jornalistas"
            subtitle="Para visualizar a lista de jornalistas cadastrados, insira um filtro, se desejar, e clique em buscar."
            dialogs={getDialogs()}>
            <>
                {getName()}
                {getCategory()}
                {getStatus()}

                <SearchButtons
                    searchStatus={searchStatus}
                    handleCleanFilter={handleCleanFilter}
                    handleSearch={handleSearch}/>

                <Grid item xs={12}>
                    {searchStatus == SearchStatus.INITIAL && 
                        <TableEmpty
                            displayText='Insira um filtro de busca acima. Os jornalistas cadastrados aparecerão aqui.'  />
                    }
                    {searchStatus == SearchStatus.LOADING &&
                        <TableLoading displayText='Carregando jornalista...' />}
                    {searchStatus == SearchStatus.RESULT && (
                        <>
                            { getAddNewButton() }
                            <TableCommon 
                                tableData={journalistContext.journalistList}
                                tableColumns={tableColumns}
                                emptyDataSourceMessage='Nenhum jornalista encontrado.'
                                tableActions={tableActions}
                                extraOptions={tableOptions}/>
                        </>
                    )}
                    {searchStatus == SearchStatus.EMPTY &&
                        <TableEmpty
                            displayText='Nenhum jornalista foi encontrado.'
                            buttonText='ADICIONAR NOVO JORNALISTA'
                            pageLocation={PageLocation.JOURNALIST_INPUT} />
                    }
                </Grid>
            </>
        </BasicPage>
    );
}